import { AccountStatus } from './Entities';
import { StipulationType } from './Stipulations';
import { WebhookEventType } from './Webhooks';

export enum MilestoneType {
  customerAgreement = 'customerAgreement',
  customerVerification = 'customerVerification',
  initialAccountDetails = 'initialAccountDetails',
  noticeToProceed = 'noticeToProceed',
  install = 'install',
  loopField = 'loopField',
  activation = 'activation',
  postActivation = 'postActivation',
  title = 'title',
  /**
   * @deprecated
   */
  installComplete = 'installComplete',
  /**
   * @deprecated
   */
  installPackageComplete = 'installPackageComplete',
  /**
   * @deprecated
   */
  systemActivation = 'systemActivation',
}

export enum MilestoneReviewStatus {
  approved = 'approved',
  rejected = 'rejected',
}

export enum MilestoneRejectionLabel {
  install = 'Install',
  activation = 'Activation',
  pto = 'PTO',
}

export const MilestoneLabels: Record<MilestoneType, string> = {
  [MilestoneType.customerAgreement]: 'Customer Agreement',
  [MilestoneType.customerVerification]: 'Customer Verification',
  [MilestoneType.initialAccountDetails]: 'Qualification',
  [MilestoneType.noticeToProceed]: 'Notice to Proceed',
  [MilestoneType.install]: 'Install',
  [MilestoneType.loopField]: 'Loop Field',
  [MilestoneType.activation]: 'Activation',
  [MilestoneType.postActivation]: 'Post Activation',
  [MilestoneType.title]: 'Title',
  [MilestoneType.installComplete]: 'Install Complete',
  [MilestoneType.installPackageComplete]: 'Install Package Complete',
  [MilestoneType.systemActivation]: 'System Activation',
};

// Display order for the account-summary milestone facet (ascending). Kept beside
// MilestoneLabels and typed as a total Record so a new MilestoneType cannot be added
// without also being given a summary order — the omission that hid the loopField bug.
export const MilestoneOrder: Record<MilestoneType, number> = {
  [MilestoneType.customerAgreement]: 0,
  [MilestoneType.customerVerification]: 0,
  [MilestoneType.initialAccountDetails]: 1,
  [MilestoneType.noticeToProceed]: 2,
  [MilestoneType.loopField]: 3,
  [MilestoneType.install]: 4,
  [MilestoneType.activation]: 5,
  [MilestoneType.postActivation]: 6,
  [MilestoneType.title]: 7,
  [MilestoneType.installComplete]: 4,
  [MilestoneType.installPackageComplete]: 4,
  [MilestoneType.systemActivation]: 5,
};

export interface MilestoneStage {
  milestone: MilestoneType;
  accountStatuses: AccountStatus[];
  milestoneWebhookEvent?: WebhookEventType;
}

export type MilestoneTemplateType =
  | MilestoneType.customerAgreement
  | MilestoneType.customerVerification
  | MilestoneType.initialAccountDetails
  | MilestoneType.noticeToProceed
  | MilestoneType.install
  | MilestoneType.loopField
  | MilestoneType.activation
  | MilestoneType.postActivation;

export interface MilestoneDocument {
  label: string;
  type: MilestoneTemplateType;
}

export interface RequirementDocument {
  label: string;
  type: MilestoneRequirement;
}

export enum MilestonePackageRoute {
  installPackage = 'installation-package',
  activationPackage = 'activation-package',
  postActivationPackage = 'post-activation-package',
  loopFieldPackage = 'loop-field-package',
}

export const MilestoneRouteToMilestoneTypeMap: Record<MilestonePackageRoute, MilestoneType> = {
  [MilestonePackageRoute.installPackage]: MilestoneType.install,
  [MilestonePackageRoute.activationPackage]: MilestoneType.activation,
  [MilestonePackageRoute.postActivationPackage]: MilestoneType.postActivation,
  [MilestonePackageRoute.loopFieldPackage]: MilestoneType.loopField,
};

export const MilestonePackageRouteToLabels: Record<MilestonePackageRoute, string> = {
  [MilestonePackageRoute.installPackage]: 'Installation Package',
  [MilestonePackageRoute.activationPackage]: 'Activation Package',
  [MilestonePackageRoute.postActivationPackage]: 'Post Activation Package',
  [MilestonePackageRoute.loopFieldPackage]: 'Loop Field Package',
};

export enum MilestoneRequirement {
  activationDocuments = 'activationDocuments',
  activationPackage = 'activationPackage',
  acceptTermsAndConditions = 'acceptTermsAndConditions',
  confirmInstalledSystem = 'confirmInstalledSystem',
  consumerAccountSignUp = 'consumerAccountSignUp',
  consumerOrderReview = 'consumerOrderReview',
  contractSigned = 'contractSigned',
  contractFinalized = 'contractFinalized',
  creditApproved = 'creditApproved',
  customerInfo = 'customerInfo',
  customerRequirements = 'customerRequirements',
  customerSignOff = 'customerSignOff',
  dxfApproved = 'dxfApproved',
  domesticContentConfirmation = 'domesticContentConfirmation',
  electricalPhotosApproved = 'electricalPhotosApproved',
  electricianSignOff = 'electricianSignOff',
  existingSystemDocumentation = 'existingSystemDocumentation',
  extendedWarranty = 'extendedWarranty',
  finalDesignApproved = 'finalDesignApproved',
  incentives = 'incentives',
  incentiveDocumentsApproved = 'incentiveDocumentsApproved',
  identityVerified = 'identityVerified',
  installPackage = 'installPackage',
  installPhotosApproved = 'installPhotosApproved',
  jobCloseOutApproved = 'jobCloseOutApproved',
  monitoringActive = 'monitoringActive',
  monitoringSiteId = 'monitoringSiteId',
  /** @deprecated */
  ntpPackage = 'ntpPackage',
  paymentMethodSubmitted = 'paymentMethodSubmitted',
  permitApproved = 'permitApproved',
  planSetApproved = 'planSetApproved',
  productionModelApproved = 'productionModelApproved',
  projectSitePhotosApproved = 'projectSitePhotosApproved',
  proposal = 'proposal',
  ptoConfirmation = 'ptoConfirmation',
  quoteActive = 'quoteActive',
  roofPhotosApproved = 'roofPhotosApproved',
  shadeReportApproved = 'shadeReportApproved',
  siteImprovements = 'siteImprovements',
  storagePhotosApproved = 'storagePhotosApproved',
  systemCommissioningApproved = 'systemCommissioningApproved',
  systemDetailsSubmitted = 'systemDetailsSubmitted',
  titleApproved = 'titleApproved',
  underwriting = 'underwriting',
  utilityBill = 'utilityBill',
  utilityPowerTurnedOnApproved = 'utilityPowerTurnedOnApproved',
  proofOfLoad = 'proofOfLoad',
  workOrderApproved = 'workOrderApproved',
  postActivationPackage = 'postActivationPackage',
  proofOfInstallation = 'proofOfInstallation',
  hvacInstallation = 'hvacInstallation',
  voltageReadings = 'voltageReadings',
  staticPressureReadings = 'staticPressureReadings',
  laserThermometerReading = 'laserThermometerReading',
  salesRepVerification = 'salesRepVerification',
  siteAssessment = 'siteAssessment',
  fsmChecklistApproved = 'fsmChecklistApproved',
  measureQuickSystemVitalsApproved = 'measureQuickSystemVitalsApproved',
  bluetoothHVACToolApproved = 'bluetoothHVACToolApproved',
  indoorSystemApproved = 'indoorSystemApproved',
  outdoorSystemApproved = 'outdoorSystemApproved',
  zoningSystemApproved = 'zoningSystemApproved',
  thermostatApproved = 'thermostatApproved',
  accessoriesApproved = 'accessoriesApproved',
  installationCommissioningDocumentsApproved = 'installationCommissioningDocumentsApproved',
  /** Geothermal: Loop Field milestone requirements */
  loopFieldPackage = 'loopFieldPackage',
  loopFieldPermitApproved = 'loopFieldPermitApproved',
  loopFieldDesignDocumentsApproved = 'loopFieldDesignDocumentsApproved',
  /** ConsumerSolar: Customer Verification milestone requirements */
  utilityBillVerified = 'utilityBillVerified',
  initialQuoteGenerated = 'initialQuoteGenerated',
  /** ConsumerSolar: NTP sub-milestone requirements */
  epcAssigned = 'epcAssigned',
  epcAccepted = 'epcAccepted',
  siteSurveyCompleted = 'siteSurveyCompleted',
  fullQuoteApproved = 'fullQuoteApproved',
  welcomeCallCompleted = 'welcomeCallCompleted',
}

export const MilestoneRequirementsToLabels: Record<MilestoneRequirement, string> = {
  [MilestoneRequirement.creditApproved]: 'Credit Approved',
  [MilestoneRequirement.titleApproved]: 'Title Approved',
  [MilestoneRequirement.systemDetailsSubmitted]: 'System Details Submitted',
  [MilestoneRequirement.contractSigned]: 'Contract Signed',
  [MilestoneRequirement.contractFinalized]: 'Contract Finalized',
  [MilestoneRequirement.finalDesignApproved]: 'Final Design Approved',
  [MilestoneRequirement.dxfApproved]: 'CAD Drawing Approved',
  [MilestoneRequirement.productionModelApproved]: 'Production Model Approved',
  [MilestoneRequirement.permitApproved]: 'Permit Approved',
  [MilestoneRequirement.shadeReportApproved]: 'Shade Report Approved',
  [MilestoneRequirement.planSetApproved]: 'Plan Set Approved',
  [MilestoneRequirement.projectSitePhotosApproved]: 'Project Site Photos Approved',
  [MilestoneRequirement.roofPhotosApproved]: 'Roof Photos Approved',
  [MilestoneRequirement.electricalPhotosApproved]: 'Electrical Photos Approved',
  [MilestoneRequirement.storagePhotosApproved]: 'Storage Photos Approved',
  [MilestoneRequirement.systemCommissioningApproved]: 'System Commissioning Approved',
  [MilestoneRequirement.jobCloseOutApproved]: 'Job Close Out Approved',
  [MilestoneRequirement.paymentMethodSubmitted]: 'Payment Method Submitted',
  [MilestoneRequirement.identityVerified]: 'Identity Verified',
  [MilestoneRequirement.utilityBill]: 'Utility Bill',
  [MilestoneRequirement.utilityPowerTurnedOnApproved]: 'Utility Power Turned On Approved',
  [MilestoneRequirement.monitoringActive]: 'Monitoring Active',
  [MilestoneRequirement.incentiveDocumentsApproved]: 'Incentive Documents Approved',
  [MilestoneRequirement.proofOfLoad]: 'Proof of Load Approved',
  [MilestoneRequirement.proofOfInstallation]: 'Proof of Installation',
  [MilestoneRequirement.hvacInstallation]: 'HVAC Installation',
  [MilestoneRequirement.voltageReadings]: 'Voltage Readings',
  [MilestoneRequirement.staticPressureReadings]: 'Static Pressure Readings',
  [MilestoneRequirement.laserThermometerReading]: 'Laser Thermometer Reading',
  [MilestoneRequirement.salesRepVerification]: 'Sales Rep Verification',
  [MilestoneRequirement.siteAssessment]: 'Site Assessment',
  [MilestoneRequirement.fsmChecklistApproved]: 'FSM Checklist Approved',
  [MilestoneRequirement.measureQuickSystemVitalsApproved]: 'Measure Quick System Vitals Approved',
  [MilestoneRequirement.bluetoothHVACToolApproved]: 'Bluetooth HVAC Tool Approved',
  [MilestoneRequirement.indoorSystemApproved]: 'Indoor System Photos Approved',
  [MilestoneRequirement.outdoorSystemApproved]: 'Outdoor System Photos Approved',
  [MilestoneRequirement.zoningSystemApproved]: 'Zoning System Photos Approved',
  [MilestoneRequirement.thermostatApproved]: 'Thermostat Photos Approved',
  [MilestoneRequirement.accessoriesApproved]: 'Accessories Photos Approved',
  [MilestoneRequirement.installationCommissioningDocumentsApproved]: 'Installation Commissioning Documents',
  [MilestoneRequirement.loopFieldPackage]: 'Loop Field Package',
  [MilestoneRequirement.loopFieldPermitApproved]: 'Permit Approved',
  [MilestoneRequirement.loopFieldDesignDocumentsApproved]: 'Design Documents',
  [MilestoneRequirement.utilityBillVerified]: 'Utility Bill Verified',
  [MilestoneRequirement.initialQuoteGenerated]: 'Initial Quote Generated',
  [MilestoneRequirement.epcAssigned]: 'EPC Assigned',
  [MilestoneRequirement.epcAccepted]: 'EPC Accepted',
  [MilestoneRequirement.siteSurveyCompleted]: 'Site Survey Completed',
  [MilestoneRequirement.fullQuoteApproved]: 'Full Quote Approved',
  [MilestoneRequirement.welcomeCallCompleted]: 'Welcome Call Completed',
  [MilestoneRequirement.acceptTermsAndConditions]: 'Accept Terms and Conditions',
  [MilestoneRequirement.activationDocuments]: 'Activation Documents',
  [MilestoneRequirement.consumerAccountSignUp]: 'Customer Account Sign Up',
  [MilestoneRequirement.consumerOrderReview]: 'Consumer Order Review/Disclosure',
  [MilestoneRequirement.customerInfo]: 'Customer Info',
  [MilestoneRequirement.customerSignOff]: 'Customer Sign Off',
  [MilestoneRequirement.domesticContentConfirmation]: 'Domestic Content Confirmation',
  [MilestoneRequirement.electricianSignOff]: 'Electrician Sign-off',
  [MilestoneRequirement.installPhotosApproved]: 'Install Photos Approved',
  [MilestoneRequirement.monitoringSiteId]: 'Monitoring Site ID',
  [MilestoneRequirement.ptoConfirmation]: 'Permission to Operate',
  [MilestoneRequirement.quoteActive]: 'Quote',
  [MilestoneRequirement.siteImprovements]: 'Site Improvement Documents',
  [MilestoneRequirement.activationPackage]: 'Activation Package',
  [MilestoneRequirement.confirmInstalledSystem]: 'Confirm Installed System',
  [MilestoneRequirement.customerRequirements]: 'Customer Requirements',
  [MilestoneRequirement.existingSystemDocumentation]: 'Existing System Documentation',
  [MilestoneRequirement.extendedWarranty]: 'Extended Warranty',
  [MilestoneRequirement.incentives]: 'Incentives',
  [MilestoneRequirement.installPackage]: 'Install Package',
  [MilestoneRequirement.postActivationPackage]: 'Post Activation Package',
  [MilestoneRequirement.proposal]: 'Proposal',
  [MilestoneRequirement.underwriting]: 'Underwriting',
  [MilestoneRequirement.workOrderApproved]: 'Work Order Approved',
  [MilestoneRequirement.ntpPackage]: 'NTP Package',
};

export const ExternalControlledRequirements: MilestoneRequirement[] = [
  MilestoneRequirement.consumerAccountSignUp,
  MilestoneRequirement.consumerOrderReview,
  MilestoneRequirement.customerSignOff,
  MilestoneRequirement.identityVerified,
];

export const IndependentlyUpdatedRequirements: MilestoneRequirement[] = [
  MilestoneRequirement.ptoConfirmation,
  MilestoneRequirement.domesticContentConfirmation,
  MilestoneRequirement.electricianSignOff,
];

export const IndependentlyUpdatedRequirementsPuertoRico: MilestoneRequirement[] = [
  MilestoneRequirement.domesticContentConfirmation,
  MilestoneRequirement.electricianSignOff,
];

export interface MilestoneRequirementObject {
  type: MilestoneRequirement;
  completed: boolean;
  completedAt?: Date;
}

export enum MilestoneStatus {
  approved = 'approved',
  rejected = 'rejected',
  resubmitted = 'resubmitted',
  submitted = 'submitted',
  pending = 'pending',
  paused = 'paused',
}

export interface Milestone {
  name?: string;
  completedAt?: Date;
  requirements: MilestoneRequirementObject[];
  status?: MilestoneStatus;
  type: MilestoneType;
}

export const MilestoneRequirementToStipulationTypeMap: {
  [
    K in
      | MilestoneRequirement.paymentMethodSubmitted
      | MilestoneRequirement.identityVerified
      | MilestoneRequirement.titleApproved
  ]: StipulationType;
} = {
  [MilestoneRequirement.paymentMethodSubmitted]: StipulationType.achRequirement,
  [MilestoneRequirement.identityVerified]: StipulationType.identityVerification,
  [MilestoneRequirement.titleApproved]: StipulationType.titleVerification,
};

export type StipulationRequirementType = keyof typeof MilestoneRequirementToStipulationTypeMap;
